import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Observable, of, Subject } from 'rxjs';
import { GaragePassage } from '../models/garage-passage.model';
import { environment } from '../../environments/environment';
import { ApiService } from '@services/api.service';
import { JwtHelperService } from '@services/jwt-helper.service';
import { LoggerService } from '@services/logger.service';
import { catchError, map } from 'rxjs/operators';
import { TableDataApiParams } from '@services/imports/import-flow.service';
import { CemCustomerModel } from '@/models/cem-customer.model';

export interface EmailPreviewCount {
  countDaInviareValid: number;
  countDaInviareInvalid: number;
  countDaNonInviare: number;
}

@Injectable({
  providedIn: 'root',
})
export class GaragePassageService extends ApiService {
  headers = new HttpHeaders();
  barerToken = '';
  pageSize = 10;
  pageNumber = 0;
  sortField = '';
  sortDirection = '';
  filter = {};
  updateList = new Subject();
  private apiUrl = `${environment.apiUrl}/cemmanagement`;
  baseUrl = this.apiUrl;

  constructor(
    protected router: Router,
    protected toastr: ToastrService,
    protected http: HttpClient,
    protected jwtHelper: JwtHelperService,
  ) {
    super(router, toastr, http, jwtHelper);
  }

  getElements(
    params: TableDataApiParams = {
      orderBy: 'DESC',
      page: 1,
      size: 15,
    },
  ): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/page`, { params: { ...params } }).pipe(
      map(({ content, ...other }) => ({
        ...other,
        content: content,
      })),
      catchError(() => of(null)),
    );
  }

  updateStatus(id: number, status: string): Observable<GaragePassage> {
    return this.http.post<GaragePassage>(`${this.apiUrl}/${id}/status`, { status }).pipe(
      map((response) => {
        LoggerService.log(response, 'updateStatus');
        return response;
      }),
    );
  }

  getEmailPreview(): Observable<EmailPreviewCount> {
    return this.http.get<EmailPreviewCount>(`${this.apiUrl}/email-preview`).pipe(
      map((response) => {
        LoggerService.log(response, 'getEmailPreview');
        return response;
      }),
    );
  }

  sendEmails(): Observable<any> {
    return this.http.post(`${this.apiUrl}/send-emails`, {}).pipe(
      map((response) => {
        LoggerService.log(response, 'sendEmails');
        return response;
      }),
    );
  }

  getElement(id: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/${id}`).pipe(
      map((response) => {
        LoggerService.log(response, 'getElement');
        return response;
      }),
    );
  }

  createElement(element: any): Observable<any> {
    return this.http.post(this.apiUrl, element).pipe(
      map((response) => {
        LoggerService.log(response, 'createElement');
        return response;
      }),
    );
  }

  updateElement(id: number, element: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/${id}`, element).pipe(
      map((response) => {
        LoggerService.log(response, 'updateElement');
        return response;
      }),
    );
  }

  deleteElement(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${id}`).pipe(
      map((response) => {
        LoggerService.log(response, 'deleteElement');
        return response;
      }),
    );
  }

  updateGaragePassage(passage: GaragePassage): Observable<GaragePassage> {
    return this.http.put<GaragePassage>(`${this.apiUrl}/${passage.id}`, passage).pipe(
      map((response) => {
        LoggerService.log(response, 'updateGaragePassage');
        return response;
      }),
    );
  }

  /**
   * Recupera la lista di clienti/utilizzatori per CEM senza paginazione
   * @param searchQuery Termine di ricerca opzionale per filtrare i risultati (minimo 3 caratteri per filtrare)
   * @returns Observable con la lista di clienti
   */
  getCemCustomers(searchQuery?: string): Observable<CemCustomerModel[]> {
    const params: any = {};
    if (searchQuery && searchQuery.length >= 3) {
      params.filter = searchQuery;
    }

    return this.http.get<CemCustomerModel[]>(`${this.apiUrl}/customers`, { params }).pipe(
      map((response: any) => {
        LoggerService.log(response, 'getCemCustomers');
        return Array.isArray(response) ? response : [];
      }),
      catchError((error) => {
        LoggerService.error('getCemCustomers failed', error);
        return of([]);
      }),
    );
  }

  reloadData(dateFrom: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/reload`, null, { params: { dateFrom } }).pipe(
      map((response) => {
        LoggerService.log(response, 'reloadData');
        return response;
      }),
      catchError((error) => {
        LoggerService.error('reloadData failed', error);
        throw error;
      }),
    );
  }
}
